/**
 * Модель changelog, его модульный кэш и отметки «прочитано».
 *
 * Кэш вынесен из DocumentationModal.tsx, потому что у него теперь два
 * потребителя: сам модал и disposeModuleState(), сбрасывающий его при
 * размонтировании ремоута. Держать его в файле с компонентами значило бы
 * экспортировать функцию из модуля компонентов — это ломает fast refresh
 * (react-refresh/only-export-components).
 *
 * Ключи «что пользователь уже прочитал» лежат здесь же, а не литералами в своих
 * компонентах: у всех трёх состояний один жизненный цикл и один сборщик —
 * disposeChangelogState(). Разъехавшийся список тут и есть баг: почищено одно
 * из трёх, остальное досталось следующему пользователю.
 */

export type ChangelogCategory = 'Added' | 'Fixed' | 'Changed' | 'Removed';

export interface ChangelogEntry {
  category: ChangelogCategory;
  bugId?: string;
  description: string;
}

export interface ChangelogVersion {
  version: string;
  date: string;
  sections: {
    title: string;
    entries: ChangelogEntry[];
  }[];
}

export interface ChangelogData {
  currentVersion: string;
  versions: ChangelogVersion[];
}

/** Версия, на которой пользователь закрыл DocumentationModal. */
export const DOCUMENTATION_LAST_READ_KEY = 'idea_calc_last_read_version';

/**
 * Версия, на которой пользователь открыл standalone-виджет VersionChangelog.
 *
 * Префикс модуля обязателен: ключ живёт в localStorage СТРАНИЦЫ (в embed — страницы
 * платформы), и `disposeChangelogState()` стирает его на каждом размонтировании.
 * Прежнее имя `changelog:last-read` не называло владельца — под ним хост или соседний
 * ремоут вполне мог держать своё, и мы стирали бы чужие данные.
 * Переименование теряет уже сохранённую отметку: индикатор непрочитанного покажется
 * один раз на текущей версии и погаснет при первом же открытии — плата за чужой ключ
 * несопоставима.
 */
export const VERSION_CHANGELOG_LAST_READ_KEY = 'idea_calc_changelog_last_read';

// Changelog is loaded dynamically from /changelog.json
let cachedChangelog: ChangelogData | null = null;

export function getCachedChangelog(): ChangelogData | null {
  return cachedChangelog;
}

export function setCachedChangelog(data: ChangelogData): void {
  cachedChangelog = data;
}

/**
 * Кэш живёт дольше дерева: он наполняется на первом открытии модала и
 * переживает unmount. Без сброса следующий mount в той же вкладке платформы —
 * уже после выката новой версии ремоута — показал бы changelog прошлой сборки.
 *
 * Отметки «прочитано» стираются вместе с ним: они переживают unmount так же, а
 * принадлежат конкретному пользователю. Оставленные, они спрятали бы от
 * следующего индикатор непрочитанных обновлений.
 */
export function disposeChangelogState(): void {
  cachedChangelog = null;
  try {
    localStorage.removeItem(DOCUMENTATION_LAST_READ_KEY);
    localStorage.removeItem(VERSION_CHANGELOG_LAST_READ_KEY);
  } catch (_) {
    /* приватный режим / отключённое хранилище — стирать нечего */
  }
}
